import {
  MAX_SELECTED_SKILLS,
  type SelectedSkillReference,
  type SkillSummary,
} from "./types";

export interface ActiveSkillSlashToken {
  start: number;
  end: number;
  caret: number;
  fragment: string;
  value: string;
}

export interface ResolvedPromptSkillToken {
  start: number;
  end: number;
  skill: SelectedSkillReference;
}

export type SkillTokenDeletionDirection = "backward" | "forward";

function isWhitespace(value: string): boolean {
  return /\s/.test(value);
}

/**
 * Finds a slash token at a collapsed caret. A token must begin at the start of
 * the draft or immediately after whitespace, and ends at the next whitespace.
 */
export function findActiveSkillSlashToken(
  value: string,
  selectionStart: number,
  selectionEnd = selectionStart
): ActiveSkillSlashToken | null {
  if (selectionStart !== selectionEnd) return null;

  const caret = Math.max(0, Math.min(selectionStart, value.length));
  let start = caret;
  while (start > 0 && !isWhitespace(value[start - 1])) {
    start -= 1;
  }

  const beforeCaret = value.slice(start, caret);
  if (!beforeCaret.startsWith("/") || beforeCaret.slice(1).includes("/")) {
    return null;
  }

  let end = caret;
  while (end < value.length && !isWhitespace(value[end])) {
    end += 1;
  }

  const tokenValue = value.slice(start, end);
  if (!tokenValue.startsWith("/") || tokenValue.slice(1).includes("/")) {
    return null;
  }

  return {
    start,
    end,
    caret,
    fragment: beforeCaret.slice(1),
    value: tokenValue,
  };
}

export function toSelectedSkillReference(skill: SkillSummary): SelectedSkillReference {
  return {
    id: skill.id,
    name: skill.name,
    slug: skill.slug,
  };
}

/**
 * Resolves prompt slash tokens against the enabled catalog. Tokens are kept in
 * draft order, duplicates are ignored, and only the first three count.
 */
export function parseResolvedSkillTokensFromPrompt(
  value: string,
  skills: readonly SkillSummary[]
): ResolvedPromptSkillToken[] {
  const skillsBySlug = new Map<string, SkillSummary>();
  for (const skill of skills) {
    if (!skill.enabled) continue;
    const key = skill.slug.toLowerCase();
    const current = skillsBySlug.get(key);
    if (!current || (skill.origin === "personal" && current.origin !== "personal")) {
      skillsBySlug.set(key, skill);
    }
  }

  const resolved: ResolvedPromptSkillToken[] = [];
  const seenIds = new Set<string>();
  const seenSlugs = new Set<string>();
  const tokenPattern = /(^|\s)(\/[^\s/]+)(?=\s|$)/g;

  for (const match of value.matchAll(tokenPattern)) {
    const token = match[2];
    const skill = skillsBySlug.get(token.slice(1).toLowerCase());
    if (!skill || seenIds.has(skill.id) || seenSlugs.has(skill.slug)) continue;

    const start = (match.index ?? 0) + match[1].length;
    resolved.push({
      start,
      end: start + token.length,
      skill: toSelectedSkillReference(skill),
    });
    seenIds.add(skill.id);
    seenSlugs.add(skill.slug);
    if (resolved.length === MAX_SELECTED_SKILLS) break;
  }

  return resolved;
}

export function parseSelectedSkillsFromPrompt(
  value: string,
  skills: readonly SkillSummary[]
): SelectedSkillReference[] {
  return parseResolvedSkillTokensFromPrompt(value, skills).map(({ skill }) => skill);
}

/**
 * Returns the atomic deletion range for a resolved token at a caret boundary.
 * At most one trailing whitespace character is included in the range.
 */
export function getResolvedSkillTokenDeletion(
  value: string,
  caret: number,
  skills: readonly SkillSummary[],
  direction: SkillTokenDeletionDirection = "backward"
): { start: number; end: number } | null {
  const safeCaret = Math.max(0, Math.min(caret, value.length));
  const tokens = parseResolvedSkillTokensFromPrompt(value, skills);

  const token = tokens.find(({ start, end }) =>
    direction === "forward"
      ? start === safeCaret
      : end === safeCaret || (end + 1 === safeCaret && isWhitespace(value[end]))
  );
  if (!token) return null;

  return {
    start: token.start,
    end: token.end + (isWhitespace(value[token.end]) ? 1 : 0),
  };
}

export function getSkillPromptInsertion(
  value: string,
  selectionStart: number,
  selectionEnd: number,
  slug: string
): { replacement: string; start: number; end: number } {
  const token = findActiveSkillSlashToken(value, selectionStart, selectionEnd);
  const needsLeadingSpace =
    !token && selectionStart > 0 && !isWhitespace(value[selectionStart - 1]);
  return {
    replacement: `${needsLeadingSpace ? " " : ""}/${slug} `,
    start: token?.start ?? selectionStart,
    end: token?.end ?? selectionEnd,
  };
}

export function getSkillSlashOptionId(listboxId: string, skillId: string): string {
  return `${listboxId}-option-${skillId.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
}
